'use client'

import {
    BalancoPatrimonialReport,
    getBalancoPatrimonialReport,
} from '@/lib/api/financial/financial-accounting-api'
import { useEffect, useState } from 'react'
import {
    MdAccountBalance,
    MdArrowBack,
    MdCheckCircle,
    MdRefresh,
    MdWarning,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const formatBrl = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export default function BalancoPatrimonialReportPage() {
    const navigate = useNavigate()
    const [asOfDate, setAsOfDate] = useState(
        new Date().toISOString().split('T')[0],
    )
    const [report, setReport] = useState<BalancoPatrimonialReport | null>(null)
    const [loading, setLoading] = useState(false)

    const load = async () => {
        setLoading(true)
        try {
            const res = await getBalancoPatrimonialReport(asOfDate)
            if (res.success && res.result) setReport(res.result)
            else toast.error(res.error || 'Erro ao carregar Balanço')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, []) // initial

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6 max-w-5xl mx-auto">
            <div className="flex items-center gap-3 flex-wrap">
                <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => navigate(-1)}
                >
                    <MdArrowBack /> Voltar
                </button>
                <div className="flex items-center gap-3 flex-1">
                    <div className="w-11 h-11 rounded-xl bg-secondary/10 flex items-center justify-center">
                        <MdAccountBalance className="text-2xl text-secondary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">
                            Balanço Patrimonial
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Ativo, Passivo e Patrimônio Líquido acumulados
                        </p>
                    </div>
                </div>
            </div>

            <div className="card bg-base-100 shadow">
                <div className="card-body p-4 flex flex-row items-end gap-3 flex-wrap">
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Data de corte</span>
                        </label>
                        <input
                            type="date"
                            className="input input-bordered input-sm"
                            value={asOfDate}
                            onChange={(e) => setAsOfDate(e.target.value)}
                        />
                    </div>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={load}
                        disabled={loading}
                    >
                        <MdRefresh /> Gerar
                    </button>
                </div>
            </div>

            {loading && (
                <div className="flex items-center justify-center min-h-[200px]">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            )}

            {!loading && report && (
                <>
                    <div
                        className={`alert ${report.totals.balanced ? 'alert-success' : 'alert-warning'}`}
                    >
                        {report.totals.balanced ? (
                            <MdCheckCircle className="text-xl" />
                        ) : (
                            <MdWarning className="text-xl" />
                        )}
                        <span className="text-sm">
                            {report.totals.balanced
                                ? 'Balanço fecha corretamente: Ativo = Passivo + PL'
                                : `Diferença de ${formatBrl(report.totals.difference)} entre Ativo e Passivo+PL. Reveja lançamentos contábeis.`}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* ATIVO */}
                        <div className="card bg-base-100 shadow">
                            <div className="card-body p-0">
                                <div className="bg-secondary/10 px-5 py-3 border-b border-base-300/50">
                                    <h2 className="font-bold text-base">
                                        ATIVO
                                    </h2>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="table table-sm">
                                        <tbody>
                                            {report.asset.accounts.map((a) => (
                                                <tr key={a.accountId}>
                                                    <td className="font-mono text-xs">
                                                        {a.code}
                                                    </td>
                                                    <td>{a.title}</td>
                                                    <td className="text-right font-mono">
                                                        {formatBrl(a.balance)}
                                                    </td>
                                                </tr>
                                            ))}
                                            {report.asset.accounts.length === 0 && (
                                                <tr>
                                                    <td
                                                        colSpan={3}
                                                        className="text-center text-base-content/50 py-6"
                                                    >
                                                        Nenhuma conta com saldo
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-base-200">
                                                <td
                                                    colSpan={2}
                                                    className="font-bold"
                                                >
                                                    Total Ativo
                                                </td>
                                                <td className="text-right font-bold font-mono">
                                                    {formatBrl(report.asset.total)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* PASSIVO + PL */}
                        <div className="card bg-base-100 shadow">
                            <div className="card-body p-0">
                                <div className="bg-error/10 px-5 py-3 border-b border-base-300/50">
                                    <h2 className="font-bold text-base">
                                        PASSIVO
                                    </h2>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="table table-sm">
                                        <tbody>
                                            {report.liability.accounts.map((a) => (
                                                <tr key={a.accountId}>
                                                    <td className="font-mono text-xs">
                                                        {a.code}
                                                    </td>
                                                    <td>{a.title}</td>
                                                    <td className="text-right font-mono">
                                                        {formatBrl(a.balance)}
                                                    </td>
                                                </tr>
                                            ))}
                                            {report.liability.accounts.length ===
                                                0 && (
                                                    <tr>
                                                        <td
                                                            colSpan={3}
                                                            className="text-center text-base-content/50 py-4"
                                                        >
                                                            Nenhuma conta de passivo
                                                        </td>
                                                    </tr>
                                                )}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-base-200">
                                                <td
                                                    colSpan={2}
                                                    className="font-bold"
                                                >
                                                    Total Passivo
                                                </td>
                                                <td className="text-right font-bold font-mono">
                                                    {formatBrl(
                                                        report.liability.total,
                                                    )}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                <div className="bg-info/10 px-5 py-3 border-y border-base-300/50">
                                    <h2 className="font-bold text-base">
                                        PATRIMÔNIO LÍQUIDO
                                    </h2>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="table table-sm">
                                        <tbody>
                                            {report.equity.accounts.map((a) => (
                                                <tr key={a.accountId}>
                                                    <td className="font-mono text-xs">
                                                        {a.code}
                                                    </td>
                                                    <td>{a.title}</td>
                                                    <td className="text-right font-mono">
                                                        {formatBrl(a.balance)}
                                                    </td>
                                                </tr>
                                            ))}
                                            <tr>
                                                <td className="font-mono text-xs">—</td>
                                                <td className="italic">
                                                    Resultado do Exercício
                                                </td>
                                                <td className="text-right font-mono">
                                                    {formatBrl(
                                                        report.equity
                                                            .netResultOfPeriod,
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-base-200">
                                                <td
                                                    colSpan={2}
                                                    className="font-bold"
                                                >
                                                    Total PL
                                                </td>
                                                <td className="text-right font-bold font-mono">
                                                    {formatBrl(
                                                        report.equity.total,
                                                    )}
                                                </td>
                                            </tr>
                                            <tr className="bg-secondary/5">
                                                <td
                                                    colSpan={2}
                                                    className="font-bold"
                                                >
                                                    Total Passivo + PL
                                                </td>
                                                <td className="text-right font-bold font-mono">
                                                    {formatBrl(
                                                        report.totals
                                                            .liabilityAndEquity,
                                                    )}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
